import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Asset imports
import heroIllustration from '../assets/hero-illustration.png';
import featureAnalytics from '../assets/feature-analytics.png';
import featureCollaboration from '../assets/feature-collaboration.png';
import featureAutomation from '../assets/feature-automation.png';
import featurePipeline from '../assets/feature-pipeline.png';
import featureClients from '../assets/feature-clients.png';
import featureTasks from '../assets/feature-tasks.png';
import featureEmail from '../assets/feature-email.png';
import featureInventory from '../assets/feature-inventory.png';
import featureTraining from '../assets/feature-training.png';
import salesToastLogo from '../assets/images/sales-toast-logo.png';

const LandingPage: React.FC = () => {
    const { user } = useAuth();
    const [activeFeature, setActiveFeature] = useState(0);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
        };
        const handleScroll = () => setScrollY(window.scrollY);

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveFeature((prev) => (prev + 1) % features.length);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    if (user) {
        return <Navigate to="/dashboard" replace />;
    }

    const features = [
        {
            title: 'Sales Pipeline',
            description: 'Visual Kanban-style deal tracking with drag-and-drop stages',
            image: featurePipeline,
            accent: '#10B981',
            bg: 'from-emerald-50 to-teal-50',
            icon: '📊',
        },
        {
            title: 'Client Hub',
            description: 'Complete CRM with profiles, history, and smart segments',
            image: featureClients,
            accent: '#8B5CF6',
            bg: 'from-purple-50 to-indigo-50',
            icon: '👥',
        },
        {
            title: 'Task Center',
            description: 'Command center for priorities, deadlines, and automation',
            image: featureTasks,
            accent: '#F59E0B',
            bg: 'from-amber-50 to-orange-50',
            icon: '✅',
        },
        {
            title: 'Email Engine',
            description: 'Templates, automated sequences, and engagement tracking',
            image: featureEmail,
            accent: '#EC4899',
            bg: 'from-pink-50 to-rose-50',
            icon: '📧',
        },
        {
            title: 'Inventory',
            description: 'Real-time stock, supplier management, and alerts',
            image: featureInventory,
            accent: '#22C55E',
            bg: 'from-green-50 to-lime-50',
            icon: '📦',
        },
        {
            title: 'Training',
            description: 'Video courses, progress tracking, and certifications',
            image: featureTraining,
            accent: '#6366F1',
            bg: 'from-indigo-50 to-blue-50',
            icon: '🎓',
        },
    ];

    const capabilities = [
        { icon: featureAnalytics, label: 'Analytics', desc: 'Real-time dashboards', color: 'from-blue-500 to-cyan-500' },
        { icon: featureCollaboration, label: 'Collaboration', desc: 'Team synergy tools', color: 'from-purple-500 to-pink-500' },
        { icon: featureAutomation, label: 'Automation', desc: 'Smart workflows', color: 'from-orange-500 to-red-500' },
    ];

    const stats = [
        { value: '10x', label: 'Faster Closing', icon: '⚡' },
        { value: '50%', label: 'More Efficiency', icon: '📈' },
        { value: '24/7', label: 'Always Available', icon: '🌐' },
        { value: '100%', label: 'Secure Data', icon: '🔒' },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-gray-900 font-sans overflow-x-hidden relative">
            {/* Animated Background */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div
                    className="absolute w-[600px] h-[600px] rounded-full opacity-40 blur-[100px] transition-all duration-700 ease-out"
                    style={{
                        background: 'radial-gradient(circle, #60A5FA 0%, #A78BFA 50%, transparent 70%)',
                        left: mousePosition.x - 300,
                        top: mousePosition.y - 300,
                    }}
                />
                <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-300/50 to-indigo-300/50 rounded-full blur-3xl animate-pulse" />
                <div className="absolute top-40 right-20 w-96 h-96 bg-gradient-to-br from-purple-300/40 to-pink-300/40 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
                <div className="absolute bottom-40 left-1/4 w-80 h-80 bg-gradient-to-br from-cyan-300/40 to-blue-300/40 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
            </div>

            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200/50 shadow-sm">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        <div className="flex items-center space-x-3">
                            <img src={salesToastLogo} alt="Sales Toast" className="w-10 h-10 object-contain" />
                            <span className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-purple-600 bg-clip-text text-transparent">Sales Toast</span>
                        </div>
                        <div className="hidden md:flex items-center space-x-8">
                            <a href="#features" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">Features</a>
                            <a href="#benefits" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">Benefits</a>
                            <a href="#stats" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">Why Us</a>
                        </div>
                        <div className="flex items-center space-x-4">
                            <Link
                                to="/login"
                                className="px-5 py-2.5 text-gray-700 hover:text-blue-600 font-semibold transition-colors"
                            >
                                Log in
                            </Link>
                            <Link
                                to="/register"
                                className="relative group px-7 py-3 font-semibold overflow-hidden rounded-xl text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-shadow"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 transition-transform group-hover:scale-105" />
                                <span className="relative">Get Started Free</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 lg:pt-44">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-center max-w-4xl mx-auto mb-20">
                        <div className="inline-flex items-center px-5 py-2.5 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 border border-blue-200 mb-8 shadow-md transform hover:scale-105 transition-transform">
                            <span className="text-lg mr-2">🚀</span>
                            <span className="text-sm font-semibold text-blue-700">The Future of Sales Management</span>
                        </div>

                        <h1 className="text-5xl lg:text-7xl font-black tracking-tight mb-8 leading-[1.1]">
                            <span className="text-gray-900">Transform Your</span>
                            <br />
                            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                Sales Journey
                            </span>
                        </h1>

                        <p className="text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed">
                            A powerful, beautiful platform that helps you <span className="text-blue-600 font-semibold">manage clients</span>,
                            <span className="text-indigo-600 font-semibold"> track deals</span>, and
                            <span className="text-purple-600 font-semibold"> close revenue</span>—all in one place.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-5 justify-center mb-16">
                            <Link
                                to="/register"
                                className="group relative inline-flex items-center justify-center px-10 py-5 text-lg font-bold rounded-2xl overflow-hidden text-white shadow-2xl shadow-blue-500/40 hover:shadow-blue-500/60 transition-all transform hover:-translate-y-1"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600" />
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
                                <span className="relative flex items-center">
                                    🎯 Start Free Trial
                                    <svg className="ml-3 w-5 h-5 group-hover:translate-x-2 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                    </svg>
                                </span>
                            </Link>
                            <Link
                                to="/login"
                                className="inline-flex items-center justify-center px-10 py-5 text-lg font-bold rounded-2xl border-2 border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all bg-white shadow-lg transform hover:-translate-y-1"
                            >
                                🔑 Sign In
                            </Link>
                        </div>
                    </div>

                    {/* Bento Grid */}
                    <div className="grid grid-cols-12 gap-5 lg:gap-7">
                        {/* Main Dashboard Preview */}
                        <div className="col-span-12 lg:col-span-8 relative group">
                            <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-[2rem] blur-xl opacity-30 group-hover:opacity-50 transition-opacity" />
                            <div className="relative bg-white rounded-[1.5rem] p-6 lg:p-8 border border-gray-200 shadow-2xl overflow-hidden">
                                <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 rounded-full blur-3xl opacity-60" />
                                <div className="absolute bottom-0 left-0 w-60 h-60 bg-gradient-to-tr from-pink-100 to-orange-100 rounded-full blur-3xl opacity-40" />
                                <img
                                    src={heroIllustration}
                                    alt="Dashboard Preview"
                                    className="relative w-full h-auto rounded-xl transform group-hover:scale-[1.02] transition-transform duration-700 shadow-lg"
                                />
                                <div className="absolute bottom-10 left-10 bg-white/90 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl border border-gray-100">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                                        <span className="text-sm font-semibold text-gray-700">Live Dashboard Preview</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Capability Cards */}
                        <div className="col-span-12 lg:col-span-4 grid grid-rows-3 gap-5 lg:gap-7">
                            {capabilities.map((cap, index) => (
                                <div
                                    key={index}
                                    className="group relative bg-white rounded-2xl p-6 border border-gray-200 shadow-xl overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-2 duration-300 cursor-pointer"
                                >
                                    <div className={`absolute -right-10 -bottom-10 w-40 h-40 bg-gradient-to-br ${cap.color} rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity`} />
                                    <div className="flex items-center space-x-4 relative">
                                        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${cap.color} flex items-center justify-center overflow-hidden shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-transform`}>
                                            <img src={cap.icon} alt={cap.label} className="w-10 h-10 object-contain" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-lg text-gray-900">{cap.label}</h3>
                                            <p className="text-sm text-gray-500 font-medium">{cap.desc}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section id="stats" className="py-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 relative overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
                    <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
                </div>
                <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Why Teams Choose Sales Toast</h2>
                        <p className="text-xl text-blue-100">Results that speak for themselves</p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <div
                                key={index}
                                className="text-center p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all transform hover:-translate-y-2 hover:scale-105"
                            >
                                <div className="text-4xl mb-3">{stat.icon}</div>
                                <div className="text-4xl lg:text-5xl font-black text-white mb-2">{stat.value}</div>
                                <div className="text-blue-100 font-semibold">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Interactive Features Showcase */}
            <section id="features" className="py-28 relative bg-gradient-to-b from-slate-50 to-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-20">
                        <span className="inline-block px-4 py-2 rounded-full bg-blue-100 text-blue-700 font-bold text-sm mb-6">✨ POWERFUL FEATURES</span>
                        <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6">
                            Everything you need. <span className="text-gray-400">Nothing you don't.</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Six powerful modules designed to streamline your entire sales operation.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        {/* Feature Image */}
                        <div className="relative order-2 lg:order-1">
                            <div
                                className="absolute -inset-6 rounded-[2rem] blur-2xl opacity-40 transition-all duration-500"
                                style={{ background: `linear-gradient(135deg, ${features[activeFeature]?.accent}60, ${features[activeFeature]?.accent}30)` }}
                            />
                            <div
                                className={`relative bg-gradient-to-br ${features[activeFeature]?.bg} rounded-[2rem] p-10 border-2 border-gray-100 shadow-2xl transition-all duration-500`}
                            >
                                <div className="absolute top-6 left-6 text-4xl">{features[activeFeature]?.icon}</div>
                                <img
                                    src={features[activeFeature]?.image}
                                    alt={features[activeFeature]?.title}
                                    className="w-full h-auto rounded-xl transition-transform duration-500 shadow-lg"
                                />
                            </div>
                        </div>

                        {/* Feature List */}
                        <div className="space-y-4 order-1 lg:order-2">
                            {features.map((feature, index) => (
                                <button
                                    key={index}
                                    onClick={() => setActiveFeature(index)}
                                    className={`w-full text-left p-6 rounded-2xl transition-all duration-300 border-2 ${activeFeature === index
                                        ? 'bg-white shadow-2xl border-blue-200 scale-[1.02]'
                                        : 'bg-gray-50/50 border-transparent hover:bg-white hover:shadow-lg hover:border-gray-200'
                                        }`}
                                >
                                    <div className="flex items-center space-x-5">
                                        <div
                                            className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl transition-all duration-300 shadow-md"
                                            style={{
                                                backgroundColor: activeFeature === index ? `${feature.accent}20` : 'white',
                                                border: activeFeature === index ? `2px solid ${feature.accent}40` : '2px solid #e5e7eb',
                                            }}
                                        >
                                            {feature.icon}
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-bold text-xl text-gray-900">{feature.title}</h3>
                                            <p className={`text-sm mt-1 transition-colors ${activeFeature === index ? 'text-gray-600' : 'text-gray-400'}`}>
                                                {feature.description}
                                            </p>
                                        </div>
                                        <div
                                            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${activeFeature === index ? 'bg-blue-600' : 'bg-gray-200'
                                                }`}
                                        >
                                            <svg
                                                className={`w-5 h-5 transition-all ${activeFeature === index ? 'text-white rotate-0' : 'text-gray-500 -rotate-90'}`}
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>

                                    {activeFeature === index && (
                                        <div className="mt-5 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full animate-progress"
                                                style={{ backgroundColor: feature.accent }}
                                            />
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Value Props */}
            <section id="benefits" className="py-28 relative bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-20">
                        <span className="inline-block px-4 py-2 rounded-full bg-purple-100 text-purple-700 font-bold text-sm mb-6">💎 KEY BENEFITS</span>
                        <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6">
                            Built for modern sales teams
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Stop juggling spreadsheets and disconnected tools. Everything works together, beautifully.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                title: 'Intuitive Design',
                                description: 'Zero learning curve. Your team will be productive from day one with our beautiful, easy-to-use interface.',
                                gradient: 'from-blue-600 to-cyan-500',
                                bg: 'from-blue-50 to-cyan-50',
                                icon: '🎨',
                            },
                            {
                                title: 'Real-time Sync',
                                description: 'Every update, everywhere, instantly. Never miss a beat with live collaboration and notifications.',
                                gradient: 'from-purple-600 to-pink-500',
                                bg: 'from-purple-50 to-pink-50',
                                icon: '⚡',
                            },
                            {
                                title: 'Smart Automation',
                                description: 'Let the platform handle the busywork with intelligent workflows while you focus on closing deals.',
                                gradient: 'from-orange-600 to-red-500',
                                bg: 'from-orange-50 to-red-50',
                                icon: '🤖',
                            },
                        ].map((card, index) => (
                            <div
                                key={index}
                                className="group relative"
                            >
                                <div className={`absolute -inset-1 bg-gradient-to-r ${card.gradient} rounded-[2rem] blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-500`} />
                                <div className={`relative bg-gradient-to-br ${card.bg} rounded-[1.5rem] p-8 lg:p-10 border-2 border-gray-100 h-full hover:shadow-2xl transition-all duration-500 hover:-translate-y-3`}>
                                    <div className={`w-20 h-20 rounded-2xl bg-gradient-to-r ${card.gradient} flex items-center justify-center mb-8 shadow-xl text-4xl transform group-hover:scale-110 group-hover:rotate-3 transition-transform`}>
                                        {card.icon}
                                    </div>
                                    <h3 className="text-2xl font-black text-gray-900 mb-4">{card.title}</h3>
                                    <p className="text-gray-600 text-lg leading-relaxed">{card.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-28 relative">
                <div className="max-w-5xl mx-auto px-6 lg:px-8">
                    <div className="relative">
                        <div className="absolute -inset-6 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-[3rem] blur-2xl opacity-30" />
                        <div className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 rounded-[2.5rem] p-12 lg:p-20 text-center overflow-hidden shadow-2xl">
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-white/10 rounded-full blur-3xl" />
                            <div className="absolute bottom-0 right-0 w-80 h-80 bg-purple-500/30 rounded-full blur-3xl" />
                            <div className="absolute top-10 left-10 text-6xl opacity-20">✨</div>
                            <div className="absolute bottom-10 right-10 text-6xl opacity-20">🚀</div>

                            <div className="relative">
                                <span className="inline-block px-4 py-2 rounded-full bg-white/20 text-white font-semibold text-sm mb-8 backdrop-blur-sm">
                                    🎉 No Credit Card Required
                                </span>
                                <h2 className="text-4xl lg:text-6xl font-black mb-8 text-white leading-tight">
                                    Ready to supercharge<br />your sales?
                                </h2>
                                <p className="text-xl lg:text-2xl text-blue-100 mb-12 max-w-2xl mx-auto">
                                    Set up your workspace in minutes. Start closing more deals today.
                                </p>

                                <div className="flex flex-col sm:flex-row gap-5 justify-center">
                                    <Link
                                        to="/register"
                                        className="group inline-flex items-center justify-center px-10 py-5 text-xl font-bold rounded-2xl bg-white text-blue-600 hover:bg-gray-50 transition-all shadow-2xl transform hover:-translate-y-1 hover:scale-105"
                                    >
                                        🚀 Create Free Account
                                        <svg className="ml-3 w-6 h-6 group-hover:translate-x-2 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                        </svg>
                                    </Link>
                                    <Link
                                        to="/login"
                                        className="inline-flex items-center justify-center px-10 py-5 text-xl font-bold rounded-2xl border-3 border-white/40 text-white hover:bg-white/10 backdrop-blur-sm transition-all transform hover:-translate-y-1"
                                    >
                                        🔑 Log In
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-16 border-t border-gray-200 bg-gradient-to-b from-white to-gray-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
                        <div className="md:col-span-2">
                            <div className="flex items-center space-x-3 mb-6">
                                <img src={salesToastLogo} alt="Sales Toast" className="w-12 h-12 object-contain" />
                                <span className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-purple-600 bg-clip-text text-transparent">Sales Toast</span>
                            </div>
                            <p className="text-gray-600 text-lg max-w-md leading-relaxed">
                                The modern sales platform that helps teams close more deals, faster. Built for ambitious sales teams.
                            </p>
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-900 mb-6 text-lg">Product</h4>
                            <ul className="space-y-4">
                                <li><a href="#features" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">Features</a></li>
                                <li><a href="#stats" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">Why Us</a></li>
                                <li><a href="#benefits" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">Benefits</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-900 mb-6 text-lg">Get Started</h4>
                            <ul className="space-y-4">
                                <li><Link to="/register" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">Sign Up Free</Link></li>
                                <li><Link to="/login" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">Log In</Link></li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center">
                        <p className="text-gray-500 font-medium">
                            © 2024 Sales Toast. All rights reserved.
                        </p>
                        <div className="flex space-x-6 mt-4 md:mt-0">
                            <a href="#" className="text-gray-500 hover:text-blue-600 transition-colors font-medium">Privacy</a>
                            <a href="#" className="text-gray-500 hover:text-blue-600 transition-colors font-medium">Terms</a>
                            <a href="#" className="text-gray-500 hover:text-blue-600 transition-colors font-medium">Support</a>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Custom Animations */}
            <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .animate-progress {
          animation: progress 4s linear;
        }
      `}</style>
        </div>
    );
};

export default LandingPage;
